/** Password-recovery sessions must not present as normal app auth until reset completes. */

export const PASSWORD_RECOVERY_ACTIVE_KEY = 'prii:password-recovery-active';

export function markPasswordRecoveryActive() {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(PASSWORD_RECOVERY_ACTIVE_KEY, '1');
}

export function clearPasswordRecoveryActive() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PASSWORD_RECOVERY_ACTIVE_KEY);
}

export function isPasswordRecoveryActive() {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(PASSWORD_RECOVERY_ACTIVE_KEY) === '1';
}

/** Hash (implicit) or PKCE query params from Supabase recovery redirect. */
export function hasRecoveryUrlParams() {
  if (typeof window === 'undefined') return false;

  const hash = window.location.hash || '';
  if (hash.includes('type=recovery') || hash.includes('access_token=')) {
    return true;
  }

  const search = new URLSearchParams(window.location.search);
  if (search.has('code') && window.location.pathname === '/reset-password') {
    return true;
  }

  return false;
}

function parseUrlHashParams() {
  const hash = window.location.hash || '';
  if (!hash) return null;
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  return new URLSearchParams(raw);
}

/** Supabase error payload in the URL hash after a failed recovery redirect. */
export function getRecoveryUrlError() {
  if (typeof window === 'undefined') return null;

  const params = parseUrlHashParams();
  if (!params) return null;

  const error = params.get('error');
  const errorCode = params.get('error_code');
  const errorDescription = params.get('error_description');

  if (!error && !errorCode && !errorDescription) {
    return null;
  }

  return { error, errorCode, errorDescription };
}

const RECOVERY_ERROR_CODE_MESSAGES = {
  otp_expired: 'This reset link has expired or was already used. Request a new one.',
};

/** User-facing copy for a recovery URL error — prefers mapped codes over raw description. */
export function formatRecoveryErrorMessage(recoveryError) {
  if (!recoveryError) {
    return 'This reset link is invalid or has expired.';
  }

  const { errorCode, errorDescription } = recoveryError;
  if (errorCode && RECOVERY_ERROR_CODE_MESSAGES[errorCode]) {
    return RECOVERY_ERROR_CODE_MESSAGES[errorCode];
  }
  if (errorDescription?.trim()) {
    return errorDescription.trim();
  }
  return 'This reset link is invalid or has expired.';
}

const OTP_VERIFY_ERROR_MESSAGES = {
  otp_expired: 'That code has expired. Request a new one.',
  token_expired: 'That code has expired. Request a new one.',
  invalid_token: 'That code is incorrect. Check the email and try again.',
  otp_disabled: 'Email codes are not enabled for this project.',
};

/** User-facing copy for verifyOtp recovery failures. */
export function formatOtpVerificationError(error) {
  if (!error) return 'Could not verify that code.';
  const code = error.code || error.error_code;
  if (code && OTP_VERIFY_ERROR_MESSAGES[code]) {
    return OTP_VERIFY_ERROR_MESSAGES[code];
  }
  const message = error.message?.trim();
  if (message) return message;
  return 'Could not verify that code.';
}

export function shouldIgnoreAuthSession(session, { event } = {}) {
  if (event === 'PASSWORD_RECOVERY') return true;
  if (!session) return false;
  if (isPasswordRecoveryActive()) return true;
  return hasRecoveryUrlParams();
}

export function toEffectiveAuthSession(session, options = {}) {
  return shouldIgnoreAuthSession(session, options) ? null : session;
}
